import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Toast } from './ui';
import { useTranslation } from '../../lib/i18n';
import {
  PROMO_CREDITS_PARAM,
  PROMO_FREE_HOURS_PARAM,
  PROMO_OUTCOME_PARAM,
  promoToastFor,
  promoToastValues,
  type PromoToast,
} from '../../lib/promo-toast';

/**
 * The only thing a campaign link visibly does.
 *
 * `/promo/<code>` grants and redirects here, so without this the reader clicks "get 800 credits",
 * watches a loader, and lands on an unchanged home screen — which reads as a failure whether it
 * worked or not.
 *
 * Mounted at the FRAME rather than on a home, because "home" is three different screens here
 * (the map home, the task-list overview, the dashboard) and the redirect must not have to know
 * which one is armed.
 *
 * The query is consumed on arrival: read once, then stripped with a `replace` so a refresh or a
 * Back does not re-announce credits that were granted minutes ago.
 */
export function PromoResultToast() {
  const router = useRouter();
  const { t } = useTranslation();
  const [toast, setToast] = useState<PromoToast | null>(null);

  const outcome = router.query[PROMO_OUTCOME_PARAM];
  const creditsParam = router.query[PROMO_CREDITS_PARAM];
  const freeHoursParam = router.query[PROMO_FREE_HOURS_PARAM];

  // Keyed on the PARAMS, not on `router.isReady`. This component is mounted by `AppFrame` under
  // `_app`, so it is already alive — and already ready — while `/promo/<code>` is redeeming. The
  // arrival it has to notice is a client-side `replace` that adds the query to a route it never
  // left, which changes no readiness flag and remounts nothing.
  useEffect(() => {
    if (!router.isReady || typeof outcome !== 'string') return;

    const credits = Number(creditsParam ?? 0);
    const freeHours = Number(freeHoursParam ?? 0);
    setToast(promoToastFor(outcome, Number.isFinite(credits) ? credits : 0, Number.isFinite(freeHours) ? freeHours : 0));

    // Strip the promo params, keeping whatever else is on the URL — the home reads its own query too.
    const rest = { ...router.query };
    delete rest[PROMO_OUTCOME_PARAM];
    delete rest[PROMO_CREDITS_PARAM];
    delete rest[PROMO_FREE_HOURS_PARAM];
    void router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, outcome, creditsParam, freeHoursParam]);

  if (!toast) return null;

  return (
    <Toast
      open
      // TOP, and it stays until dismissed. This is the only feedback a campaign link gives, and the
      // reader did not trigger it from the screen they are looking at — they arrived here from a
      // redirect. A 4-second pill at the bottom is missable, and if it is missed the whole click
      // looks like it did nothing.
      placement="top"
      dismissible
      autoHideDuration={null}
      severity={toast.severity}
      // Numbers are formatted client-side, where the locale is known — the same reason the
      // package picker does not let the server group its digits.
      message={t(toast.key, promoToastValues(toast))}
      onClose={() => setToast(null)}
    />
  );
}
